/* Detailing1 — Preis im "In den Warenkorb"-Button + Preis-Block + Backorder-Label.
 * - Button zeigt aktueller Preis × Menge (nie Strichpreis).
 * - Preis-Block (.price__current / .price__was) zeigt IMMER den Stueckpreis der
 *   Variante — bewusst OHNE Mengen-Multiplikation (Vorgabe Jasper, 2026-09-01).
 *   Er wird nur noch bei echter Preisaenderung nachgezogen (Variantenwechsel,
 *   Bundle-Update), nicht beim Aendern der Menge.
 * - Backorder-Variante -> Button-Label auf Vorbestell-Text.
 * Quelle pro Variante: eine saubere, marktrichtige `| money`-Map (superscript-/
 * waehrungssicher). */
if (!customElements.get('atc-price')) {
  class AtcPrice extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('.js-product');
      this.form = this.closest('.js-product-form-main') || this.closest('form');
      if (!this.form) return;

      this.button = this.form.querySelector('[name="add"]');
      this.amountEl = this.form.querySelector('.js-atc-price');
      this.labelEl = this.form.querySelector('.js-atc-label');
      this.qtyInput = this.form.querySelector('[name="quantity"]');
      this.preorderText = this.dataset.preorderText || '';

      // Preis-Block (Stueckpreis, wird NICHT mit der Menge hochgerechnet)
      this.priceCurrentEl = this.product ? this.product.querySelector('.product-info__price .price__current') : null;
      this.priceWasEl = this.product ? this.product.querySelector('.product-info__price .price__was') : null;

      this.data = this.readData();                 // { "<id>": { p: "19,99€", bo: true } }
      this.unit = this.dataset.unitPrice || '';     // aktueller Variantenpreis (formatiert)
      this.available = this.dataset.available !== 'false';
      this.capturePriceUnits();

      if (!this.button || !this.amountEl) return;

      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => this.render());
        this.qtyInput.addEventListener('input', () => this.render());
      }
      if (this.product) {
        // Handler merken und beim Abhaengen wieder entfernen: seit der Variantenwechsel
        // den ATC-Block serverseitig austauscht, wuerden sonst alte (abgehaengte)
        // atc-price-Elemente weiter mitlauschen und den Preis mit ihrem Altstand ueberschreiben.
        this.boundVariantChange = (e) => this.onVariantChange(e);
        this.product.addEventListener('on:variant:change', this.boundVariantChange);
      }

      // Initial: aktuelle Variante backorder? -> Vorbestell-Label.
      // Via rAF, sonst ueberschreibt der variant-picker-Konstruktor (laeuft spaeter) das Label.
      const cur = this.currentEntry();
      if (this.available && cur && cur.bo) {
        requestAnimationFrame(() => this.setBackorderLabel());
      }

      this.render();
    }

    disconnectedCallback() {
      if (this.product && this.boundVariantChange) {
        this.product.removeEventListener('on:variant:change', this.boundVariantChange);
      }
    }

    readData() {
      try {
        const el = this.querySelector('.js-atc-price-data');
        return el ? JSON.parse(el.textContent) : {};
      } catch (e) {
        return {};
      }
    }

    currentEntry() {
      const idEl = this.form.querySelector('[name="id"]');
      const id = idEl ? idEl.value : null;
      return id ? this.data[id] : null;
    }

    /** Liest die Stueckpreise (Menge 1), die das Theme gerade gesetzt hat. */
    capturePriceUnits() {
      if (this.priceCurrentEl) this.priceUnit = this.priceCurrentEl.textContent.trim();
      if (this.priceWasEl) this.compareUnit = this.priceWasEl.textContent.trim();
    }

    setBackorderLabel() {
      if (this.labelEl && this.preorderText) this.labelEl.textContent = this.preorderText;
    }

    onVariantChange(evt) {
      if (!this.isConnected) return;
      const variant = evt.detail && evt.detail.variant;
      if (!variant || !variant.available) {
        this.available = false;
        // Bugfix: Auch fuer ausverkaufte Varianten den vom Theme (variant-picker)
        // frisch gesetzten Stueckpreis neu einlesen -- sonst ueberschreibt render()
        // den korrekten Preis mit dem der vorher gewaehlten Variante.
        this.capturePriceUnits();
        this.render();
        return;
      }
      this.available = true;
      const entry = this.data[variant.id];
      if (entry && entry.p) this.unit = entry.p;
      // Preis-Block-Stueckpreise nach dem Theme-Update neu einlesen
      this.capturePriceUnits();
      // Backorder-Label (sonst hat variant-picker schon das normale Label gesetzt)
      if (entry && entry.bo) this.setBackorderLabel();
      this.render();
    }

    render() {
      // Abgehaengte Instanzen duerfen den (gemeinsamen) Preisblock nicht mehr anfassen.
      if (!this.isConnected) return;
      const qty = Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1);

      // Button-Preis
      if (this.amountEl) {
        const total = (this.available && this.unit) ? AtcPrice.scaleMoney(this.unit, qty) : null;
        if (total == null) {
          this.amountEl.textContent = '';
          this.amountEl.hidden = true;
        } else {
          this.amountEl.hidden = false;
          this.amountEl.textContent = total;
        }
      }

      // Preis-Block: Stueckpreis + Vergleichspreis OHNE Mengen-Multiplikation.
      // Geschrieben wird nur, wenn sich der Wert wirklich unterscheidet — so
      // laesst eine reine Mengenaenderung den Preisblock voellig unberuehrt
      // (kein DOM-Write, keine MutationObserver-Kette), waehrend Variantenwechsel
      // und Bundle-Updates (Biscuits setzt priceUnit/compareUnit + render()) weiter
      // sauber durchschlagen.
      if (this.priceCurrentEl && this.priceUnit) {
        if (this.priceCurrentEl.textContent.trim() !== this.priceUnit) {
          this.priceCurrentEl.textContent = this.priceUnit;
        }
      }
      if (this.priceWasEl) {
        const was = this.compareUnit || '';
        if (this.priceWasEl.textContent.trim() !== was) {
          this.priceWasEl.textContent = was;
        }
      }
    }

    /**
     * Multipliziert einen formatierten Geldstring mit einer ganzen Zahl und behaelt
     * Waehrungssymbol, Tausender- und Dezimaltrenner exakt bei (de/EUR, GBP, CHF, ...).
     */
    static scaleMoney(formatted, qty) {
      if (!formatted) return null;
      const str = String(formatted).replace(/<[^>]*>/g, '').trim();
      const m = str.match(/^(\D*?)(\d(?:[\d.,\s]*\d)?)(\D*)$/);
      if (!m) return null;

      const prefix = m[1];
      const rawCore = m[2];
      const suffix = m[3];

      const compact = rawCore.replace(/\s/g, '');
      const dm = compact.match(/([.,])(\d{1,2})$/);
      const dec = dm ? dm[1] : '';
      const decPlaces = dm ? dm[2].length : 0;

      const digits = rawCore.replace(/\D/g, '');
      if (!digits) return null;

      let group = '';
      for (const ch of rawCore) {
        if (ch !== dec && (ch === '.' || ch === ',' || /\s/.test(ch))) {
          group = /\s/.test(ch) ? ' ' : ch;
          break;
        }
      }
      if (!group) group = dec === ',' ? '.' : (dec === '.' ? ',' : '');

      const totalMinor = parseInt(digits, 10) * qty;
      let intPart;
      let fracPart = '';
      if (decPlaces > 0) {
        const s = String(totalMinor).padStart(decPlaces + 1, '0');
        intPart = s.slice(0, -decPlaces);
        fracPart = s.slice(-decPlaces);
      } else {
        intPart = String(totalMinor);
      }
      if (group) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, group);

      return prefix + (fracPart ? intPart + dec + fracPart : intPart) + suffix;
    }
  }

  customElements.define('atc-price', AtcPrice);
}

